import { type Home, type ResidentItem, ResidentStatus } from '@lmc/ts-common'
import { EmptyState, NoDataIcon } from '@lmc/ui-kit'
import useHomeType from 'hooks/useHomeType'
import useZones from 'hooks/useZones'
import _ from 'lodash'
import type React from 'react'
import { useState } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import Actions from '../../../../actions'
import { getNominative } from '../../../../common/utils'
import { Spinner } from '../../../../components/generic'
import Operations from '../../../../operations'
import { history } from '../../../../store'
import {
  ResidentListHeader,
  ResidentListItem,
  ResidentSideFilters
} from './components'
import useResidents from './hooks/useResidents'
import { matchesSearch } from './utils'

const StyledRowWrapper = styled.div`
  display: flex;
  margin-top: 0px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    max-height: max-content;
    overflow-y: auto;
    overflow-x: hidden;
  }
`

const StyledMainContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: auto;
  margin-right: auto;
  max-width: 2000px;
  height: calc(100vh - 109px);
  margin-left: auto;
  margin-right: auto;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    height: calc(100vh - 63px);
  }
`

const StyledUserInfoContainer = styled.div`
  padding-top: 54px;
  margin: 0 24px;
  height: 100%;
`

const StyledScrollerDesktop = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 160px);
  overflow-y: auto;
  width: 100%;
`

type ResidentListProps = {
  testId?: string
}

const ResidentList: React.FC<ResidentListProps> = ({ testId }) => {
  const dispatch = useDispatch()

  const [search, setSearch] = useState<string>('')

  const [zones, setZones] = useState<string[]>([])

  const [remarks, setRemarks] = useState<string[]>([])

  const [statuses, setStatuses] = useState<string[]>(['active', 'dummy'])

  const { residents, isLoading } = useResidents()
  // const { residents, isLoading, refetch } = useResidentsList()

  const { isPro } = useHomeType()

  const features = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const countableResidents =
    residents?.filter(
      (resident) =>
        resident.status &&
        [
          ResidentStatus.active,
          ResidentStatus.inactive,
          ResidentStatus.hospitalised
        ].includes(resident.status)
    ) || []

  const residentCount = countableResidents.length

  const { zones: zonesData } = useZones()

  const { t } = useTranslation()

  const openModal = () => {
    const id = 'addResident'
    dispatch(Actions.openModal({ modalId: id }))
    dispatch(Actions.addForm({ formId: id }))
  }

  const hasResidents = !!residents?.length

  const searchResults = _.filter(residents, (resident) =>
    matchesSearch(resident, statuses, zones, remarks, search)
  )

  if (isLoading)
    return (
      <Spinner
        data-testid={testId ? `${testId}-loading` : 'residents-list-loading'}
      />
    )

  const hasSearchResults = !!searchResults?.length

  const residentList = hasSearchResults ? (
    searchResults.map((resident: ResidentItem, index: number) => (
      <ResidentListItem
        firstItem={index === 0}
        key={resident.id}
        resident={resident}
        testId={testId}
        index={index}
        onClick={(e: { stopPropagation: () => void }) => {
          e.stopPropagation()
          history.push(`/residents/${resident.id}/profile`)
        }}
      />
    ))
  ) : (
    <EmptyState
      title={t(`general.emptyState.noResidentsListResultTitle`, {
        variable: getNominative(
          preferredPeopleNominative,
          'Resident'
        )?.toLowerCase()
      })}
      description={t(`general.emptyState.noTeamListResultDescription`)}
      icon={<NoDataIcon />}
      data-testid={
        testId ? `${testId}-no-results` : 'residents-list-no-results'
      }
    />
  )

  const handleSubscribePro = () => {
    dispatch(Operations.openProPlanCheckout({}))
  }

  return (
    <StyledMainContainer
      data-testid={testId ? `${testId}-residents-list` : 'residents-list'}
    >
      <ResidentListHeader
        openModal={openModal}
        onSearch={setSearch}
        title={getNominative(preferredPeopleNominative, 'Residents')}
        searchPlaceholder={t(`general.team.typeToSearch`)}
        buttonTitle={t(`general.resident.addServiceUser`, {
          variable: getNominative(preferredPeopleNominative, 'Resident')
        })}
        isPro={isPro}
        residentCount={residentCount}
        preferredPeopleNominative={preferredPeopleNominative}
        testId={testId}
      />
      {hasResidents ? (
        <StyledRowWrapper
          data-testid={
            testId ? `${testId}-residents-content` : 'residents-content'
          }
        >
          <ResidentSideFilters
            clearButtonTitle={t(`general.buttons.clear`)}
            features={features}
            noZoneTitle={t(`general.team.noZone`)}
            onDefaultOpened={isDesktop}
            onRemarks={setRemarks}
            onStatuses={setStatuses}
            onSubscribe={handleSubscribePro}
            onZones={setZones}
            remarks={remarks}
            remarksTitle={t(`general.resident.remarks`)}
            residents={residents}
            statuses={statuses}
            statusTitle={t(`general.team.status`)}
            testId={testId}
            viewProTitle={t(`general.team.viewPro`)}
            zones={zones}
            zonesData={zonesData}
            zoneTitle={t(`general.team.zone`)}
          />

          {isDesktop ? (
            <StyledScrollerDesktop
              data-cy="residents-list"
              data-testid={
                testId ? `${testId}-residents-scroller` : 'residents-scroller'
              }
            >
              {residentList}
            </StyledScrollerDesktop>
          ) : (
            <div
              data-testid={
                testId
                  ? `${testId}-residents-mobile-list`
                  : 'residents-mobile-list'
              }
            >
              {residentList}
            </div>
          )}
        </StyledRowWrapper>
      ) : (
        <StyledUserInfoContainer
          data-testid={testId ? `${testId}-no-residents` : 'no-residents'}
        >
          <EmptyState
            title={t(`general.emptyState.noServiceUsersResponseTitle`, {
              variable: getNominative(
                preferredPeopleNominative,
                'Residents'
              )?.toLowerCase()
            })}
            description={t(
              `general.emptyState.noServiceUsersResponseDescription`,
              {
                variable: getNominative(
                  preferredPeopleNominative,
                  'Residents'
                )?.toLowerCase()
              }
            )}
            icon={<NoDataIcon />}
            data-testid={
              testId ? `${testId}-empty-state` : 'residents-empty-state'
            }
          />
        </StyledUserInfoContainer>
      )}
    </StyledMainContainer>
  )
}

const isDesktop = isBrowser || isTablet

export default ResidentList
